import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import {
  filterWellsFromList,
  setIsMidstreamNavigatorFilter,
  setWellListExtent
} from "store/features";
import {
  filterWellsFromList as filterFacilitiesFromList,
  setWellListExtent as setFacilityWellListExtent,
  setIsMidstreamNavigatorFilter as setIsMidstreamNavigatorFacilityFilter
} from "store/features/filter/facilityFilterSlice";
import { RootState } from "store/rootReducer";

import { EntityKind } from "models/entityKind";

import useGetContributingWells from "components/map/hooks/useGetContributingWellsForFacilities";
import { useMapContext } from "components/map/hooks/useMapContext";

import { useFilterUndo } from "./useFilterUndo";

/**
 * Hook for filtering to contributing wells from all facilities.
 */
export const useContributingWellsFromAllFacilitiesFilter = () => {
  const dispatch = useDispatch();
  const getContributingWells = useGetContributingWells();

  const activeEntityKinds = useSelector(
    (state: RootState) => state.app.activeEntityKinds
  );

  const { facilitiesInGroup } = useMapContext();
  const { undoFilters } = useFilterUndo();

  // Get the contributing wells data from the mutation
  const contributingWellsData = getContributingWells.data;

  const filterToContributingWellsFromAllFacilities = useCallback(async () => {
    if (!activeEntityKinds.includes(EntityKind.Facility)) {
      toast.warn(
        "Facilities must be enabled to use this filter. Please enable facilities in the map."
      );
      return;
    }

    const facilityIds: string[] = [];
    for (const title of Object.keys(facilitiesInGroup)) {
      const group = facilitiesInGroup[title];
      for (const id of Object.keys(group.uwis)) {
        facilityIds.push(id);
      }
    }

    if (facilityIds.length === 0) {
      toast.warn(
        "No facilities found in current map view to filter contributing wells from."
      );
      return;
    }

    try {
      const contributingWells = await getContributingWells.mutateAsync(facilityIds);
      if (contributingWells) {
        const wellIds = contributingWells.map((well) => well.wellId);
        if (wellIds.length === 0) {
          toast.warn("No contributing wells found for facilities.");
          return;
        }

        dispatch(setIsMidstreamNavigatorFilter(true));
        dispatch(setIsMidstreamNavigatorFacilityFilter(true));

        dispatch(filterWellsFromList({ wellList: wellIds, resetMidstreamFlag: false }));
        dispatch(
          filterFacilitiesFromList({ wellList: facilityIds, resetMidstreamFlag: false })
        );
      }
    } catch (error) {
      toast.error("Failed to fetch contributing wells.");
    }
  }, [getContributingWells, dispatch, activeEntityKinds, facilitiesInGroup]);
  const removeContributingWellsFromAllFacilitiesFilter = async (
    isToggled: boolean = false
  ) => {
    // Clear the filter extents to prevent unwanted zooming after undoing midstream filters
    if (isToggled) {
      dispatch(undoFilters());
    } else {
      dispatch(setWellListExtent(undefined));
      dispatch(setFacilityWellListExtent(undefined));
    }
  };
  return {
    filterToContributingWellsFromAllFacilities,
    removeContributingWellsFromAllFacilitiesFilter,
    facilitiesInGroup,
    networkFraction: contributingWellsData
  };
};
